import { ethers } from 'ethers';
import { encodeFunctionData } from 'viem';

import useWrappedRPC from './useWrappedRPC';
import useEmbeddedWallet from './useEmbeddedWallet';
import useToken from './useToken';
import { abiPaymentGateway } from '../config/abi.contract';
import {
  getGoldPurchaseSignature,
  validateGoldPurchaseTransaction,
  getPackPurchaseSignature,
  validatePackPurchaseTransaction,
  getDivingTicketPurchaseSignature,
  validateDivingTicketPurchaseTransaction,
} from '../services/transaction.service';
import environments from '../utils/environments';

const {
  FISHING_FRENZY_PURCHASE_GATEWAY_CONTRACT_ADDRESS,
  FISH_TOKEN_CONTRACT_ADDRESS,
  XFISH_TOKEN_CONTRACT_ADDRESS,
} = environments;

export const tokens = {
  RON: ethers.constants.AddressZero,
  FISH: FISH_TOKEN_CONTRACT_ADDRESS,
  xFISH: XFISH_TOKEN_CONTRACT_ADDRESS,
};

function usePurchase() {
  const { checkAndApproveToken } = useToken();
  const { WrappedJsonProvider, sendTransactionWithGas, customChainNetwork } = useWrappedRPC();
  const { getEmbeddedWallet } = useEmbeddedWallet();

  const purchaseGold = async (_amount) => {
    try {
      await getEmbeddedWallet();

      // default is RON
      const paymentTokenFE = ethers.constants.AddressZero;
      const res = await getGoldPurchaseSignature({ amount: _amount, paymentToken: paymentTokenFE });

      const {
        rawAmount: amount,
        purchaseType,
        paymentToken,
        packId,
        quantity,
        nonce,
        deadline,
        refType,
        referrer,
        signature,
      } = res.data;

      const parsedAmount = ethers.utils.parseEther(`${amount}`);
      const data = encodeFunctionData({
        abi: abiPaymentGateway,
        functionName: 'purchase',
        args: [
          purchaseType,
          paymentToken,
          parsedAmount,
          packId,
          quantity,
          nonce,
          deadline,
          refType,
          referrer,
          signature,
        ],
      });

      const transactionRequest = {
        to: FISHING_FRENZY_PURCHASE_GATEWAY_CONTRACT_ADDRESS,
        data,
        value: parsedAmount,
      };

      let tokenString;
      for (let key of Object.keys(tokens)) {
        if (tokens[key].toLowerCase() === paymentToken.toLowerCase()) tokenString = key;
      }
      const txUiConfig = {
        header: `Purchase Gold`,
        description: `Send ${amount} ${tokenString} to purchase gold`,
        buttonText: `Send`,
      };

      const receipt = await sendTransactionWithGas(transactionRequest, txUiConfig);
      if (!receipt) throw new Error('Transaction failed on blockchain');

      const res1 = await validateGoldPurchaseTransaction({ transactionHash: receipt.hash });

      return {
        success: true,
        message: 'Purchase gold successful',
        data: res1.data,
      };
    } catch (error) {
      console.error('Purchase gold transaction failed:', error);
      return { success: false, message: error.message };
    }
  };

  const purchasePack = async (_amount, rawPackId, packName, token) => {
    try {
      const embeddedWallet = await getEmbeddedWallet();

      const paymentTokenFE = tokens[token] || FISH_TOKEN_CONTRACT_ADDRESS;
      const res = await getPackPurchaseSignature({ packId: rawPackId, paymentToken: paymentTokenFE });
      const {
        rawAmount: amount,
        purchaseType,
        paymentToken,
        packId,
        quantity,
        nonce,
        deadline,
        refType,
        referrer,
        signature,
      } = res.data;

      const parsedAmount = ethers.utils.parseEther(`${amount}`);
      const data = encodeFunctionData({
        abi: abiPaymentGateway,
        functionName: 'purchase',
        args: [
          purchaseType,
          paymentToken,
          parsedAmount,
          packId,
          quantity,
          nonce,
          deadline,
          refType,
          referrer,
          signature,
        ],
      });

      const isNativeToken = paymentToken === ethers.constants.AddressZero;

      const transactionRequest = {
        to: FISHING_FRENZY_PURCHASE_GATEWAY_CONTRACT_ADDRESS,
        data,
      };
      if (!isNativeToken)
        await checkAndApproveToken({
          tokenAddress: paymentToken,
          ownerAddress: embeddedWallet.address,
          spenderAddress: FISHING_FRENZY_PURCHASE_GATEWAY_CONTRACT_ADDRESS,
          amount,
        });
      else transactionRequest.value = parsedAmount;

      const txUiConfig = {
        header: `Purchase ${packName}`,
        description: `Send ${amount} ${token} to purchase ${packName}`,
        buttonText: `Send`,
      };

      const receipt = await sendTransactionWithGas(transactionRequest, txUiConfig);
      if (!receipt) throw new Error('Transaction failed on blockchain');

      const res1 = await validatePackPurchaseTransaction({ transactionHash: receipt.hash, packId: rawPackId });

      return {
        success: true,
        message: `Purchase ${packName} successful`,
        data: res1.data,
      };
    } catch (error) {
      console.error('Purchase starter pack transaction failed:', error);
      return { success: false, message: error.message };
    }
  };

  const purchaseDivingTicket = async (mode, token) => {
    try {
      const embeddedWallet = await getEmbeddedWallet();

      const paymentTokenFE = tokens[token] || FISH_TOKEN_CONTRACT_ADDRESS;
      const res = await getDivingTicketPurchaseSignature({ mode, paymentToken: paymentTokenFE });

      const {
        rawAmount: amount,
        purchaseType,
        paymentToken,
        packId,
        quantity,
        nonce,
        deadline,
        refType,
        referrer,
        signature,
      } = res.data;

      const parsedAmount = ethers.utils.parseEther(`${amount}`);
      const data = encodeFunctionData({
        abi: abiPaymentGateway,
        functionName: 'purchase',
        args: [
          purchaseType,
          paymentToken,
          parsedAmount,
          packId,
          quantity,
          nonce,
          deadline,
          refType,
          referrer,
          signature,
        ],
      });

      const isNativeToken = paymentToken === ethers.constants.AddressZero;

      const transactionRequest = {
        to: FISHING_FRENZY_PURCHASE_GATEWAY_CONTRACT_ADDRESS,
        data,
      };
      if (!isNativeToken)
        await checkAndApproveToken({
          tokenAddress: paymentToken,
          ownerAddress: embeddedWallet.address,
          spenderAddress: FISHING_FRENZY_PURCHASE_GATEWAY_CONTRACT_ADDRESS,
          amount,
        });
      else transactionRequest.value = parsedAmount;

      const txUiConfig = {
        header: `Purchase diving ticket`,
        description: `Send ${amount} ${token} to purchase a diving ticket`,
        buttonText: `Send`,
      };

      const receipt = await sendTransactionWithGas(transactionRequest, txUiConfig);
      if (!receipt) throw new Error('Transaction failed on blockchain');

      const res1 = await validateDivingTicketPurchaseTransaction({ transactionHash: receipt.hash, mode });

      return {
        success: true,
        message: res1.data.result.message,
        data: res1.data,
      };
    } catch (error) {
      console.error('Purchase starter pack transaction failed:', error);
      return { success: false, message: error.message };
    }
  };

  return { purchaseGold, purchasePack, purchaseDivingTicket };
}

export default usePurchase;
